import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { ActionMenu } from '~/components/ui/ActionMenu';
import { Button } from '~/components/ui/Button';
import { IconButton } from '~/components/ui/IconButton';
import { Input } from '~/components/ui/Input';
import { Table, TableHead, TableRow } from '~/components/ui/Table';
import { getPackagePrompts, updatePackagePrompt, deletePackagePrompt } from '~/lib/.client/api/organizations';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import type { OrganizationData } from '~/types/organization';
import { logger } from '~/utils/logger';
import { withSpinner } from '~/utils/withSpinner';

interface PackagePromptsSectionProps {
  organization: Pick<OrganizationData, 'provider' | 'id'>;
}

export function PackagePromptsSection({ organization }: PackagePromptsSectionProps) {
  const [showPromptForm, setShowPromptForm] = useState(false);
  const [editingPromptIndex, setEditingPromptIndex] = useState<number | null>(null);
  const [packagePrompts, setPackagePrompts] = useState<Array<{ packageName: string; prompt: string }>>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState<number | null>(null);

  const [promptForm, setPromptForm] = useState({
    packageName: '',
    prompt: '',
  });

  useEffect(() => {
    const loadPrompts = async () => {
      try {
        const prompts = await getPackagePrompts({ organization });
        setPackagePrompts(prompts);
      } catch (error) {
        if (error instanceof Error && error.message) {
          toast.error(error.message);
        }

        logger.error('Failed to load package prompts:', error);
      }
    };

    loadPrompts();
  }, [organization]);

  const handlePromptSave = async () => {
    withSpinner({
      action: async () => {
        try {
          if (editingPromptIndex !== null) {
            const originalPrompt = packagePrompts[editingPromptIndex];

            const hasChanges =
              originalPrompt.packageName !== promptForm.packageName || originalPrompt.prompt !== promptForm.prompt;

            if (!hasChanges) {
              setShowPromptForm(false);
              setEditingPromptIndex(null);
              setPromptForm({ packageName: '', prompt: '' });

              return;
            }

            await updatePackagePrompt({
              organization,
              packageName: promptForm.packageName,
              prompt: promptForm.prompt,
            });

            const newPrompts = [...packagePrompts];
            newPrompts[editingPromptIndex] = { packageName: promptForm.packageName, prompt: promptForm.prompt };
            setPackagePrompts(newPrompts);

            sendAnalyticsEvent({
              action: AnalyticsAction.Track,
              payload: {
                event: AnalyticsTrackEvent.PackagePromptUpdated,
                properties: {
                  packageName: promptForm.packageName,
                  promptLength: promptForm.prompt.length,
                },
              },
            });
          } else {
            await updatePackagePrompt({
              organization,
              packageName: promptForm.packageName,
              prompt: promptForm.prompt,
            });
            setPackagePrompts([...packagePrompts, { packageName: promptForm.packageName, prompt: promptForm.prompt }]);

            sendAnalyticsEvent({
              action: AnalyticsAction.Track,
              payload: {
                event: AnalyticsTrackEvent.PackagePromptAdded,
                properties: {
                  packageName: promptForm.packageName,
                  promptLength: promptForm.prompt.length,
                },
              },
            });
          }

          setShowPromptForm(false);
          setEditingPromptIndex(null);
          setPromptForm({ packageName: '', prompt: '' });
        } catch (error) {
          if (error instanceof Error && error.message) {
            toast.error(error.message);
          }

          logger.error('Failed to save package prompt:', error);
        }
      },
      onStateUpdate: setIsSaving,
    });
  };

  const handlePromptDelete = async (index: number) => {
    withSpinner({
      action: async () => {
        try {
          const prompt = packagePrompts[index];
          await deletePackagePrompt({
            organization,
            packageName: prompt.packageName,
          });

          const newPrompts = packagePrompts.filter((_, idx) => idx !== index);
          setPackagePrompts(newPrompts);

          sendAnalyticsEvent({
            action: AnalyticsAction.Track,
            payload: {
              event: AnalyticsTrackEvent.PackagePromptDeleted,
              properties: {
                packageName: prompt.packageName,
                promptLength: prompt.prompt.length,
              },
            },
          });

          setShowPromptForm(false);
          setEditingPromptIndex(null);
          setPromptForm({ packageName: '', prompt: '' });
        } catch (error) {
          if (error instanceof Error && error.message) {
            toast.error(error.message);
          }

          logger.error('Failed to delete package prompt:', error);
        }
      },
      onStateUpdate: (isSpinning) => setIsDeleting(isSpinning ? index : null),
    });
  };

  const handleEditPrompt = (index: number) => {
    const prompt = packagePrompts[index];

    if (prompt) {
      setPromptForm({
        packageName: prompt.packageName,
        prompt: prompt.prompt,
      });
      setEditingPromptIndex(index);
      setShowPromptForm(true);
    }
  };

  if (showPromptForm) {
    return (
      <div className="w-full mt-10">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <IconButton
              icon="i-heroicons:arrow-left-solid"
              size="md"
              variant="ghost"
              onClick={() => {
                setShowPromptForm(false);
                setEditingPromptIndex(null);
              }}
              aria-label="Back"
            />
            <span className="font-semibold text-lg leading-tight">
              {editingPromptIndex !== null ? 'Edit package instructions' : 'New package instructions'}
            </span>
          </div>
          {editingPromptIndex !== null && (
            <Button
              size="sm"
              variant="ghost"
              color="danger"
              className="text-red-500"
              onClick={() => handlePromptDelete(editingPromptIndex)}
              disabled={isDeleting === editingPromptIndex}
            >
              Delete package instruction
            </Button>
          )}
        </div>
        <div className="flex flex-col w-full gap-6 rounded-xl">
          <div>
            <label className="block text-sm font-medium mb-1">Package Name</label>
            <Input
              type="text"
              value={promptForm.packageName}
              onChange={(e) => setPromptForm({ ...promptForm, packageName: e.target.value })}
              placeholder="@acme/ui"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              required
              inputClassName="bg-bolt-elements-background-depth-1"
            />
            <div className="text-xs text-bolt-elements-textSecondary mt-1">
              Enter the exact package name as it appears in your package.json (e.g., @acme/ui, react, lodash)
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Package Prompt</label>
            <textarea
              value={promptForm.prompt}
              onChange={(e) => setPromptForm({ ...promptForm, prompt: e.target.value })}
              placeholder="Enter package-specific instructions for Bolt..."
              rows={4}
              className="w-full p-2.5 text-sm bg-bolt-elements-background-depth-1 border border-bolt-elements-border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-bolt-elements-item-outlineActive placeholder-bolt-elements-textTertiary"
            />
            <div className="text-xs text-bolt-elements-textSecondary mt-1">
              This prompt will be used when working with this specific package
            </div>
          </div>
          <div className="flex gap-2 justify-end mt-4">
            <Button
              size="md"
              variant="secondary"
              disabled={isSaving}
              onClick={() => {
                setShowPromptForm(false);
                setEditingPromptIndex(null);
              }}
            >
              Cancel
            </Button>
            <Button
              size="md"
              variant="primary"
              color="brand"
              loading={isSaving}
              onClick={handlePromptSave}
              disabled={!promptForm.prompt.trim() || !promptForm.packageName.trim()}
            >
              {editingPromptIndex !== null ? 'Save changes' : 'Save new prompt'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full mt-10">
      <div className="flex items-start justify-between mb-6 gap-4 mt-4">
        <div className="flex flex-col justify-center">
          <span className="font-semibold text-lg leading-tight">Packages knowledge</span>
          <span className="text-sm text-bolt-elements-textSecondary leading-tight mt-3">
            Document how each package in your team is meant to be used. Bolt leverages this context to make more
            accurate decisions when using this package in an application.
          </span>
        </div>
        <Button
          size="md"
          variant="primary"
          color="brand"
          startIcon="i-heroicons:plus text-base"
          onClick={() => {
            setShowPromptForm(true);
            setEditingPromptIndex(null);
            setPromptForm({ packageName: '', prompt: '' });
          }}
        >
          Add package instructions
        </Button>
      </div>
      {packagePrompts.length > 0 ? (
        <Table className="w-full text-sm">
          <TableHead>
            <div className="flex w-full gap-x-4">
              <div className="flex-1 font-normal">Package Name</div>
              <div className="flex-none min-w-[48px] font-normal text-right">Action</div>
            </div>
          </TableHead>
          {packagePrompts.map((prompt, idx) => (
            <TableRow
              key={prompt.packageName}
              className="flex w-full gap-x-4 hover:bg-bolt-elements-background-depth-3 transition-colors"
            >
              <div className="flex-1 flex flex-col justify-center">
                <span
                  className="text-sm font-normal truncate overflow-hidden whitespace-nowrap font-mono"
                  title={prompt.packageName}
                >
                  {prompt.packageName}
                </span>
                <span
                  className="text-xs text-bolt-elements-textSecondary mt-0.5 truncate overflow-hidden whitespace-nowrap max-w-[300px]"
                  title={prompt.prompt}
                >
                  {prompt.prompt}
                </span>
              </div>
              <div className="flex-none min-w-[48px] flex items-center justify-end">
                <ActionMenu
                  triggerNode={<span className="i-heroicons:ellipsis-horizontal-solid text-xl cursor-pointer" />}
                  triggerClassName="bg-transparent w-min !p-0 outline-none"
                  items={[
                    {
                      id: 'edit',
                      label: 'Edit',
                      icon: <span className="i-heroicons:pencil-square text-base mr-2" />,
                      onAction: () => handleEditPrompt(idx),
                    },
                    {
                      id: 'delete',
                      label: 'Delete',
                      icon: <span className="i-heroicons:trash text-base mr-2" />,
                      type: 'error',
                      onAction: () => handlePromptDelete(idx),
                      disabled: isDeleting === idx,
                    },
                  ]}
                />
              </div>
            </TableRow>
          ))}
        </Table>
      ) : null}
    </div>
  );
}
